module AppKitchen {
    export module Data {

        // ReSharper disable once InconsistentNaming
        export interface EventDataLoadOptions extends LoadOptionsBase {
            success?: (data: { [key: string]: any }[]) => void;
            error?: (request: JQueryXHR) => void;
        }

        // ReSharper disable once InconsistentNaming
        export interface EventDataSaveOptions extends LoadOptionsBase {
            success?: (result: boolean | Object) => void;
            error?: (request: JQueryXHR) => void;
        }

        // ReSharper disable once InconsistentNaming
        export interface EventDataLoaderOptions {
            eventDataApi?: string;
            eventDataSaveApi?: string;
            startupFailed?: (request: JQueryXHR) => void;
        }

        // ReSharper disable once InconsistentNaming
        export interface EventDataLoaderAttributes {
            data?: {[key: string]: any}[];
            items?: AppKitchen.Api.Models.EventItemInfo[];
            ready?: boolean;
            loading?: boolean;
        }

        export class EventDataLoader extends AppKitchen.ModelBase<EventDataLoaderAttributes> {
            config: AppKitchen.Api.Models.EventDataRequest;
            itemRequest: JQueryXHR;
            dataRequest: JQueryXHR;
            dataSaveRequest: JQueryXHR;
            options: EventDataLoaderOptions;
            private itemsDict: { [key: string]: AppKitchen.Api.Models.EventItemInfo };

            constructor(config: AppKitchen.Api.Models.EventDataRequest, onReady: (loader: EventDataLoader) => void, options?: EventDataLoaderOptions) {
                super({
                    data: [],
                    items: [],
                    loading: false,
                    ready: false
                });

                this.options = AppKitchen.OptionsHelper.merge(options, {
                    eventDataApi: "EventData",
                    eventDataSaveApi: "EventDataMultiSave"
                });

                this.config = config;

                this.bind("change:ready", loader => {
                    if (loader.get().ready) {
                        onReady(this);
                    }
                });

                this.bind("change:items", () => this.updateItemsDict());
                this.loadItems();
            }

            loadItems() {
                this.itemRequest = Api.getEventItemInfos(this.config, items =>
                {
                    this.set({
                        items: items,
                        ready: true
                    });
                }, request => {
                    if (this.options.startupFailed) {
                        this.options.startupFailed(request);
                    }
                });
            }

            loadData(options?: EventDataLoadOptions) {
                options = AppKitchen.OptionsHelper.merge(options, {
                    silent: false,
                    success: () => {},
                    error: () => {}
                });

                // abort previous request
                this.abortDataRequest();

                if (!options.silent) {
                    this.set({ loading: true });
                }

                // start new request
                var api = options.api || this.options.eventDataApi;
                if (api === "EventData") {
                    this.dataRequest = Api.getEventData(this.config, (data) => {
                        this.processLoadedData(data);
                        this.set({ loading: false });
                        options.success(data);
                    }, (request) => {
                        this.set({ loading: false });
                        options.error(request);
                    });
                } else {
                    this.dataRequest = Data.getData(api, this.config, (data) => {
                        this.processLoadedData(data);
                        this.set({ loading: false });
                        options.success(data);
                    }, (request) => {
                        this.set({ loading: false });
                        options.error(request);
                    });
                }
            }

            saveData(data: { [key: string]: any }[], options?: EventDataSaveOptions) {
                if (!data || data.length === 0) {
                    throw "no data to save";
                }

                options = AppKitchen.OptionsHelper.merge(options, {
                    silent: false,
                    success: () => { },
                    error: () => { }
                });

                // abort previous request
                this.abortDataSaveRequest();

                if (!options.silent) {
                    this.set({ loading: true });
                }

                // start new request
                var api = options.api || this.options.eventDataSaveApi;
                if (api === "EventDataMultiSave") {
                    this.dataSaveRequest = Api.updateEventMultiData(this.getMultiSaveRequestData(data),
                        (saveResult: boolean | Object) => {
                            this.set({ loading: false });
                            options.success(saveResult);
                        },
                        (request) => {
                            this.set({ loading: false });
                            options.error(request);
                        });
                } else {
                    this.dataSaveRequest = Data.getData(api,
                        this.getMultiSaveRequestData(data),
                        (saveResult: boolean | Object) => {
                            this.set({ loading: false });
                            options.success(saveResult);
                        },
                        (request) => {
                            this.set({ loading: false });
                            options.error(request);
                        });
                }
            }

            getMultiSaveRequestData(data: { [key: string]: any }[]): AppKitchen.Api.Models.EventDataMultiSaveRequest {
                return {
                    EventDataSaveRequests: data.AsLinq().Select(d => this.getSaveRequestData(d)).ToArray()
                }
            }

            getSaveRequestData(data: { [key: string]: any }): AppKitchen.Api.Models.EventDataSaveRequest {
                var saveData = [];
                Object.keys(data).forEach(key => {
                    if (!this.itemsDict[key] && AppKitchen.GlobalSettings.devMode) {
                        AppKitchen.logWarning("Unknown event item key: " + key);
                    }
                    var value = data[key];
                    if (value == null) {
                        value = "";
                    } else if (value instanceof Date) {
                        value = value.toLocalISOString();
                    }
                    else if (typeof value === "boolean") {
                        value = value ? "true" : "false";
                    } else if (typeof value === "number") {
                        value = value.toString();
                    } else if (typeof value !== "string"){
                        throw "Event value with key '" + key + "' has invalid type";
                    }
                    saveData.push({ Key: key, Value: value });
                });

                return {
                    EventItemSpecs: this.config.EventItemSpecs,
                    EventValues: saveData
                }
            }

            abortRequests() {
                if (this.itemRequest) {
                    this.itemRequest.abort();
                }

                this.abortDataRequest();
                this.abortDataSaveRequest();
            }

            protected abortDataRequest() {
                if (this.dataRequest) {
                    this.dataRequest.abort();
                    this.set({ loading: false });
                }
            }

            protected abortDataSaveRequest() {
                if (this.dataSaveRequest) {
                    this.dataSaveRequest.abort();
                    this.set({ loading: false });
                }
            }

            protected processLoadedData(data: { [key: string]: any }[]) {
                // convert strings to corresponding types (date, number)
                if (data) {
                    var eventItems = this.getItemsDict();
                    for (let i = 0; i < data.length; i++) {
                        // iterate properties
                        for (let key in data[i]) {
                            if (data[i].hasOwnProperty(key) && eventItems.hasOwnProperty(key)) {
                                // convert types (e.g. date)
                                switch (eventItems[key].Type) {
                                    case "Date":
                                    case "PeriodNr":
                                        data[i][key] = kendo.parseDate(data[i][key], "yyyy-MM-ddTHH:mm:ss");
                                        break;
                                    case "Number":
                                    case "Calculated":
                                        data[i][key] = kendo.parseFloat(data[i][key], "de-DE"); // REST API has fixed "de-DE" culture
                                        break;
                                    case "Boolean":
                                        if (data[i][key] != null && typeof data[i][key] !== "boolean")
                                            data[i][key] = data[i][key] === "true";
                                        break;
                                    default:
                                        data[i][key] = data[i][key] || "";
                                        break;
                                }
                            }
                        }
                    }
                }

                this.parseData(data);
                this.set({ data: data });
            }

            protected parseData(data: { [key: string]: any }[]): void {

            }

            private  updateItemsDict() {
                this.itemsDict = this.getItemsDict();
            }

            getItemsDict(): { [key: string]: AppKitchen.Api.Models.EventItemInfo } {
                var dict: { [key: string]: AppKitchen.Api.Models.EventItemInfo } = {};
                var eventItems: AppKitchen.Api.Models.EventItemInfo[] = this.get().items;

                for (let i = 0; i < eventItems.length; i++) {
                    var key = eventItems[i].InventoryId + "_" + eventItems[i].Id;
                    dict[key] = eventItems[i];
                }

                return dict;
            }
        }

    }
}
